import { getDailyReadings, getHourlyReadings } from '@/services/consumerService'
import {
  getConsumerNotifications,
  type NotificationAlert,
} from '@/services/notificationService'
import {
  getMeterActivities,
  type MeterActivity,
  type MeterActivityType,
} from '@/services/meterActivityService'
import { describeNotificationChannel } from '@/services/notificationChannels'
import { describeNotificationStatus } from '@/services/notificationStatuses'
import { getWaterTreeZonePeriods } from '@/services/dashboardService'
import { toApiPage } from '@/utils/serverPagination'
import type { MeterTrees, TreeButtonState } from '@/utils/premiseTree'

export type DetailsTabKey =
  | 'daily'
  | 'hourly'
  | 'sharedParent'
  | 'sharedChild'
  | 'alerts'
  | 'activity'

export const DETAILS_TAB_ORDER: DetailsTabKey[] = [
  'daily',
  'hourly',
  'sharedParent',
  'sharedChild',
  'alerts',
  'activity',
]

export const detailsTabFromIndex = (index: number): DetailsTabKey =>
  DETAILS_TAB_ORDER[
    Math.min(DETAILS_TAB_ORDER.length - 1, Math.max(0, Math.trunc(index)))
  ]

export const isSharedTab = (
  tab: DetailsTabKey
): tab is 'sharedParent' | 'sharedChild' =>
  tab === 'sharedParent' || tab === 'sharedChild'

/** The tree a shared tab reads: the meter against its parent, or against the tree it supplies. */
export const treeOfTab = (
  tab: DetailsTabKey,
  trees: MeterTrees
): TreeButtonState | null => {
  if (tab === 'sharedParent') return trees.parent
  if (tab === 'sharedChild') return trees.supplier
  return null
}

/** What a tab needs before it can ask the Gateway for anything. */
export const NO_PARENT_TREE_NOTICE = 'המד אינו צרכן באף עץ מים.'
export const NO_SUPPLIED_TREE_NOTICE = 'המד אינו ספק של אף עץ מים.'

export type DetailsTabNeed = 'premise' | 'meter' | 'water-tree'

export const detailsTabNeed = (tab: DetailsTabKey): DetailsTabNeed => {
  if (tab === 'activity') return 'meter'
  if (isSharedTab(tab)) return 'water-tree'
  return 'premise'
}

export type DetailsTabRow = Record<string, unknown> & { id: number | string }

export type DetailsTabPage = { rows: DetailsTabRow[]; total: number }

export type DetailsSelection = {
  premiseId: number | null
  meterNumber: string | number | null
}

export type DetailsTabQuery = {
  siteId: string | number
  premiseId: number
  meterNumber: string | number | null
  /** The tree a shared tab reads; none until the meter's trees are known. */
  treeId?: number | null
  startDate: string
  endDate: string
  /** 0-based, as the grid counts pages. */
  page: number
  pageSize: number
}

export const mapConsumerAlertRow = (
  alert: NotificationAlert
): DetailsTabRow => ({
  id: alert.alertId,
  warningCode: alert.alertTypeId,
  alertCode: alert.alertTypeName,
  opening: alert.openedAt,
  closing: alert.closedAt,
  channels: alert.channels,
  status: alert.status,
})

export const ACTIVITY_TYPE_LABELS: Record<MeterActivityType, string> = {
  alert: 'התראה',
  notification: 'הודעה',
  crmTask: 'משימת CRM',
}

const ALERT_STATUS_LABELS: Record<string, string> = {
  open: 'פתוח',
  closed: 'סגור',
  suppressed: 'מושתק',
}

const activityName = (activity: MeterActivity): string | null => {
  if (activity.type === 'notification' && activity.title)
    return describeNotificationChannel(activity.title).label
  return activity.title
}

const activityStatus = (activity: MeterActivity): string | null => {
  if (!activity.status) return null
  if (activity.type === 'alert')
    return ALERT_STATUS_LABELS[activity.status] ?? activity.status
  if (activity.type === 'notification')
    return describeNotificationStatus(activity.status).label
  return activity.statusName ?? activity.status
}

export const mapMeterActivityRow = (
  activity: MeterActivity,
  meterNumber: string | number
): DetailsTabRow => ({
  id: `${activity.type}-${activity.refId}`,
  activityType: activity.type,
  date: activity.at,
  type: ACTIVITY_TYPE_LABELS[activity.type] ?? activity.type,
  meterNumber,
  activity: activityName(activity),
  activityDescription: activity.description,
  status: activityStatus(activity),
})

const EMPTY_PAGE: DetailsTabPage = { rows: [], total: 0 }

// Explicit, because the Gateway's hourly default sorts by start reading, not by time.
const READINGS_SORT_BY = { daily: 'daily_day', hourly: 'hourly_time' } as const

/** The readings and alerts readers resolve with a failure instead of rejecting. */
class DetailsTabRequestError extends Error {
  constructor(tab: DetailsTabKey) {
    super(`The ${tab} tab request failed`)
    this.name = 'DetailsTabRequestError'
  }
}

/**
 * One page of the selected consumer's rows for a tab. Rejects when the
 * endpoint fails, so the caller can tell "nothing to show" apart from
 * "the Gateway could not answer".
 */
export const loadDetailsTabPage = async (
  tab: DetailsTabKey,
  query: DetailsTabQuery
): Promise<DetailsTabPage> => {
  const { siteId, premiseId, startDate, endDate, page, pageSize } = query
  const paging = { page: toApiPage(page), size: pageSize }

  if (tab === 'daily' || tab === 'hourly') {
    const read = tab === 'daily' ? getDailyReadings : getHourlyReadings
    const response = await read(siteId, premiseId, {
      ...paging,
      from: startDate,
      to: endDate,
      sortBy: READINGS_SORT_BY[tab],
      sortOrder: 'DESC',
    })
    if (!response.ok) throw new DetailsTabRequestError(tab)
    return {
      rows: response.data.rows as unknown as DetailsTabRow[],
      total: response.data.total,
    }
  }

  if (tab === 'alerts') {
    const response = await getConsumerNotifications(siteId, premiseId, {
      ...paging,
      startDate,
      endDate,
      sortBy: 'createdAt',
      sortOrder: 'DESC',
      includeClosed: true,
    })
    if (response.failed) throw new DetailsTabRequestError(tab)
    return {
      rows: (response.result ?? []).map(mapConsumerAlertRow),
      total: response.total ?? 0,
    }
  }

  if (tab === 'activity') {
    if (query.meterNumber == null || query.meterNumber === '') return EMPTY_PAGE
    const meterNumber = query.meterNumber
    const response = await getMeterActivities(siteId, meterNumber, {
      ...paging,
      startDate,
      endDate,
    })
    return {
      rows: response.rows.map(row => mapMeterActivityRow(row, meterNumber)),
      total: response.total,
    }
  }

  if (isSharedTab(tab)) {
    if (query.treeId == null) return EMPTY_PAGE
    const response = await getWaterTreeZonePeriods(siteId, query.treeId, {
      zoneType: 1,
      period: 'DAILY',
      startDate,
      endDate,
      ...paging,
      sortBy: 'startDate',
      sortOrder: 'DESC',
    })
    return {
      rows: response.rows.map(row => ({ ...row, id: row.startDate })),
      total: response.total,
    }
  }

  return EMPTY_PAGE
}

/**
 * The sentence the tab shows instead of a grid. `null` means the grid can be
 * shown — including when it is legitimately empty, which the grid itself says.
 */
export const resolveDetailsTabNotice = (
  tab: DetailsTabKey,
  selection: DetailsSelection | null,
  failed: boolean,
  trees?: MeterTrees
): string | null => {
  if (!selection || selection.premiseId == null) {
    return 'בחרו צרכן מהרשימה כדי להציג נתונים.'
  }
  if (
    detailsTabNeed(tab) !== 'premise' &&
    (selection.meterNumber == null || selection.meterNumber === '')
  ) {
    return tab === 'activity'
      ? 'לצרכן שנבחר אין מספר מד, ולכן אין דרך לאחזר את הפעילויות שלו.'
      : 'לצרכן שנבחר אין מספר מד, ולכן אין דרך לאתר את עצי המים שלו.'
  }
  const tree = trees ? treeOfTab(tab, trees) : null
  if (tree?.status === 'none') {
    return tab === 'sharedParent'
      ? NO_PARENT_TREE_NOTICE
      : NO_SUPPLIED_TREE_NOTICE
  }
  if (failed || tree?.status === 'failed') {
    return 'הנתונים לא נטענו. נסו שוב.'
  }
  return null
}

export const detailsTabEmptyLabel = (tab: DetailsTabKey): string => {
  if (tab === 'daily') return 'אין קריאות יומיות בטווח התאריכים'
  if (tab === 'hourly') return 'אין קריאות שעתיות בטווח התאריכים'
  if (tab === 'alerts') return 'אין התראות לצרכן בטווח התאריכים'
  if (tab === 'activity') return 'אין פעילויות למד בטווח התאריכים'
  if (isSharedTab(tab)) return 'אין נתוני צריכה משותפת בטווח התאריכים'
  return 'אין נתונים'
}
